import { Dumbbell, Clock, MessageCircle, CalendarCheck } from 'lucide-react';
import type { TabPracticeReinforcement } from '../../../../types/craft';
import FormattedText from '../../../common/FormattedText';

interface Props {
  data: TabPracticeReinforcement;
}

const TYPE_STYLES = {
  drill: { bg: 'bg-app-focus/10', text: 'text-app-focus', label: 'Drill' },
  reflection: { bg: 'bg-app-warning/10', text: 'text-app-warning', label: 'Reflection' },
  role_play: { bg: 'bg-app-positive/10', text: 'text-app-positive', label: 'Role Play' },
  written: { bg: 'bg-app-canvas', text: 'text-app-ink', label: 'Written' },
  observation: { bg: 'bg-app-focus/10', text: 'text-app-focus', label: 'Observation' },
};

export default function PracticeReinforcementTab({ data }: Props) {
  if (!data?.exercises?.length && !data?.manager_coaching_notes?.length) {
    return (
      <div className="text-center py-16">
        <p className="text-sm text-app-muted">No data available yet.</p>
        <p className="text-xs text-app-muted mt-1">Generate this module to populate content.</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {data.exercises?.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Dumbbell className="w-4 h-4 text-app-focus" />
            <h4 className="text-sm font-semibold text-app-ink">Practice Exercises</h4>
          </div>
          <div className="space-y-3">
            {data.exercises.map((ex, i) => {
              const typeStyle = TYPE_STYLES[ex.type] || TYPE_STYLES.drill;
              return (
                <div key={i} className="bg-app-surface border border-app-line rounded-lg p-4">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <h5 className="text-sm font-semibold text-app-ink">{ex.title}</h5>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${typeStyle.bg} ${typeStyle.text}`}>
                        {typeStyle.label}
                      </span>
                      {ex.expected_duration_minutes > 0 && (
                        <span className="flex items-center gap-0.5 text-[10px] text-app-muted">
                          <Clock className="w-2.5 h-2.5" />
                          {ex.expected_duration_minutes}m
                        </span>
                      )}
                    </div>
                  </div>

                  <p className="text-xs text-app-ink leading-relaxed mb-3">{ex.instructions}</p>

                  {ex.coaching_cue && (
                    <div className="bg-forest-50 border border-forest-100 rounded-lg px-3 py-2">
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <MessageCircle className="w-3 h-3 text-forest-600" />
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-forest-600">Coaching Cue</span>
                      </div>
                      <p className="text-xs text-forest-800 leading-relaxed">{ex.coaching_cue}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {data.manager_coaching_notes?.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <MessageCircle className="w-4 h-4 text-app-positive" />
            <h4 className="text-sm font-semibold text-app-ink">Manager Coaching Notes</h4>
          </div>
          <div className="space-y-2">
            {data.manager_coaching_notes.map((note, i) => (
              <div key={i} className="flex items-start gap-2 bg-app-positive/10 border border-app-positive rounded-lg p-3">
                <span className="w-1.5 h-1.5 rounded-full bg-app-positive mt-1.5 flex-shrink-0" />
                <p className="text-xs text-app-positive leading-relaxed">{note}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {data.reinforcement_schedule && (
        <div className="bg-app-canvas border border-app-line rounded-lg p-5">
          <div className="flex items-center gap-2 mb-2">
            <CalendarCheck className="w-4 h-4 text-app-muted" />
            <h4 className="text-sm font-semibold text-app-ink">Reinforcement Schedule</h4>
          </div>
          <FormattedText text={data.reinforcement_schedule} className="text-sm text-app-ink" />
        </div>
      )}
    </div>
  );
}
